import React, { useState, useEffect } from 'react';
import { useModalDismiss } from '../../hooks/useModalDismiss';
import { useParams, useNavigate } from 'react-router-dom';
import axios from '../../Api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight, faSearch, faCheckCircle, faClock, faUser, faCalendar, faVolumeUp, faFileAlt, faInfoCircle, faTimes, faTimesCircle, faFilter, faFileExcel, faPause } from '@fortawesome/free-solid-svg-icons';

const FullControlSubmissions = () => {
  const { surveyId } = useParams();
  const navigate = useNavigate();
  const [submissions, setSubmissions] = useState([]);
  const [survey, setSurvey] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [localSearchTerm, setLocalSearchTerm] = useState(''); // Local state for input
  const [statusFilter, setStatusFilter] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [checkDateFilter, setCheckDateFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState(''); // '', 'human', 'ai'
  const [pendingStatusFilter, setPendingStatusFilter] = useState('');
  const [pendingDateFilter, setPendingDateFilter] = useState('');
  const [pendingCheckDateFilter, setPendingCheckDateFilter] = useState('');
  const [pendingSourceFilter, setPendingSourceFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalSubmissions, setTotalSubmissions] = useState(0);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const assignmentDismiss = useModalDismiss(() => setSelectedAssignment(null), { enabled: !!selectedAssignment });
  const [stats, setStats] = useState({
    total: 0,
    controlled: 0,
    pending: 0,
    inProgress: 0,
    rejected: 0,
    hold: 0
  });
  const submissionsPerPage = 20;

  useEffect(() => {
    fetchSurvey();
  }, [surveyId]);

  useEffect(() => {
    fetchSubmissions();
  }, [surveyId, currentPage, searchTerm, statusFilter, dateFilter, checkDateFilter, sourceFilter]);

  const fetchSurvey = async () => {
    try {
      // Get survey info from control surveys endpoint
      const response = await axios.get(`/control/surveys/full-control?per_page=1000`);
      const surveys = response.data.data || response.data || [];
      const foundSurvey = surveys.find(s => s.id === parseInt(surveyId));
      if (foundSurvey) {
        setSurvey(foundSurvey);
      }
    } catch (error) {
      console.error('Error fetching survey:', error);
    }
  };

  const fetchSubmissions = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage,
        per_page: submissionsPerPage,
        survey_id: surveyId,
        is_full_control: true
      });
      
      if (searchTerm) {
        params.append('search', searchTerm);
      }
      
      if (statusFilter) {
        params.append('status', statusFilter);
      }

      if (dateFilter) {
        params.append('date', dateFilter);
      }

      if (checkDateFilter) {
        params.append('check_date', checkDateFilter);
      }

      if (sourceFilter) {
        params.append('source', sourceFilter);
      }

      const response = await axios.get(`/control/assignments?${params.toString()}`);
      
      if (response.data.data) {
        setSubmissions(response.data.data);
        setTotalPages(response.data.last_page || 1);
        setTotalSubmissions(response.data.total || response.data.data.length);
        
        // Use stats from backend if available, otherwise calculate from current page
        if (response.data.stats) {
          setStats({
            total: response.data.stats.total || 0,
            controlled: response.data.stats.controlled || 0,
            pending: response.data.stats.pending || 0,
            inProgress: response.data.stats.inProgress || 0,
            rejected: response.data.stats.rejected || 0,
            hold: response.data.stats.hold || 0
          });
        } else {
          setStats({
            total: response.data.data.length,
            controlled: 0,
            pending: 0,
            inProgress: 0,
            rejected: 0,
            hold: 0
          });
        }
      } else {
        setSubmissions(response.data);
        setTotalPages(1);
        setTotalSubmissions(response.data.length);
        setStats({
          total: response.data.length,
          controlled: 0,
          pending: 0,
          inProgress: 0,
          rejected: 0
        });
      }
    } catch (error) {
      console.error('Error fetching submissions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleSearch = () => {
    setSearchTerm(localSearchTerm);
    setCurrentPage(1);
  };

  const handleSearchInputChange = (value) => {
    setLocalSearchTerm(value);
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleApplyFilters = () => {
    setStatusFilter(pendingStatusFilter);
    setDateFilter(pendingDateFilter);
    setCheckDateFilter(pendingCheckDateFilter);
    setSourceFilter(pendingSourceFilter);
    setCurrentPage(1);
  };

  const handleResetFilters = () => {
    setPendingStatusFilter('');
    setPendingDateFilter('');
    setPendingCheckDateFilter('');
    setPendingSourceFilter('');
    setStatusFilter('');
    setDateFilter('');
    setCheckDateFilter('');
    setSourceFilter('');
    setCurrentPage(1);
  };

  const handleExport = async () => {
    try {
      const params = new URLSearchParams({
        survey_id: surveyId,
        is_full_control: true
      });
      
      if (searchTerm) {
        params.append('search', searchTerm);
      }
      
      if (statusFilter) {
        params.append('status', statusFilter);
      }

      if (dateFilter) {
        params.append('date', dateFilter);
      }

      if (checkDateFilter) {
        params.append('check_date', checkDateFilter);
      }

      const response = await axios.get(`/control/assignments/export?${params.toString()}`, {
        responseType: 'blob'
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const filename = `controles_full_control_${surveyId}_${new Date().toISOString().split('T')[0]}.xlsx`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting submissions:', error);
      alert('Erreur lors de l\'exportation. Veuillez réessayer.');
    }
  };

  const handleViewDetails = async (assignmentId) => {
    try {
      const response = await axios.get(`/control/assignments/${assignmentId}`);
      // Handle both response formats: {assignment: {...}} or direct assignment object
      setSelectedAssignment(response.data.assignment || response.data);
    } catch (error) {
      console.error('Error fetching assignment details:', error);
      alert('Erreur lors du chargement des détails');
    }
  };

  // Stats are now fetched from backend and stored in state

  // Determine effective display status from assignment
  const getEffectiveStatus = (submission) => {
    // If resp_controle / AI controlled it, show their action
    if (submission.resp_controle_check) {
      const a = submission.resp_controle_check.action;
      if (a === 'reject') return 'rejected';
      // AI-hold rows are displayed as plain "En Attente" per spec, not "Mis en Attente"
      if (a === 'hold') return submission.resp_controle_check.is_ai ? 'pending' : 'hold';
      return 'done';
    }
    if (submission.status === 'done' && submission.checks && submission.checks.length > 0) {
      const sortedChecks = [...submission.checks].sort((a, b) =>
        new Date(b.checked_at || 0) - new Date(a.checked_at || 0)
      );
      if (sortedChecks[0]?.action === 'reject') {
        return 'rejected';
      }
    }
    // Held items carry a negative priority; their status may have drifted to in_progress when
    // re-opened, but they still belong in "Mis en Attente" — never show them as "En Cours".
    if (submission.status !== 'done' && (submission.priority ?? 0) < 0) {
      return 'hold';
    }
    return submission.status;
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'done':
        return (
          <span className="px-2 inline-flex items-center text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
            <FontAwesomeIcon icon={faCheckCircle} className="mr-1" />
            Contrôlée
          </span>
        );
      case 'rejected':
        return (
          <span className="px-2 inline-flex items-center text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
            <FontAwesomeIcon icon={faTimesCircle} className="mr-1" />
            Rejetée
          </span>
        );
      case 'in_progress':
        return (
          <span className="px-2 inline-flex items-center text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
            <FontAwesomeIcon icon={faClock} className="mr-1" />
            En Cours
          </span>
        );
      case 'pending':
        return (
          <span className="px-2 inline-flex items-center text-xs leading-5 font-semibold rounded-full bg-orange-100 text-orange-800">
            <FontAwesomeIcon icon={faClock} className="mr-1" />
            En Attente
          </span>
        );
      case 'hold':
        return (
          <span className="px-2 inline-flex items-center text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800">
            <FontAwesomeIcon icon={faClock} className="mr-1" />
            Mis en Attente
          </span>
        );
      default:
        return (
          <span className="px-2 inline-flex items-center text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
            {status}
          </span>
        );
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 relative overflow-hidden">
      {/* Dotted Background */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle, #3b82f6 1px, transparent 1px)',
          backgroundSize: '20px 20px'
        }}></div>
      </div>
      
      {/* Header */}
      <header className="relative z-10 p-4 sm:p-6 animate-fade-in">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 gap-4">
            <div className="flex-1">
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-2">
                {survey?.title || 'Soumissions'}
              </h1>
              <p className="text-gray-600 text-sm sm:text-base">
                Liste des soumissions à contrôler en mode contrôle complet
              </p>
            </div>
            <button
              onClick={handleExport}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center justify-center gap-2 w-full sm:w-auto"
              title="Exporter en Excel"
            >
              <FontAwesomeIcon icon={faFileExcel} />
              <span>Exporter en Excel</span>
            </button>
          </div>
        </div>
      </header>

      {/* Statistics Cards */}
      <div className="relative z-10 p-6">
        <div className="max-w-7xl mx-auto mb-6">
          <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
            <div className="bg-white/80 backdrop-blur-md rounded-xl shadow-lg border border-white/20 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
                </div>
                <FontAwesomeIcon icon={faCheckCircle} className="text-3xl text-gray-500" />
              </div>
            </div>
            <div className="bg-white/80 backdrop-blur-md rounded-xl shadow-lg border border-white/20 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Contrôlées</p>
                  <p className="text-2xl font-bold text-green-600">{stats.controlled}</p>
                </div>
                <FontAwesomeIcon icon={faCheckCircle} className="text-3xl text-green-500" />
              </div>
            </div>
            <div className="bg-white/80 backdrop-blur-md rounded-xl shadow-lg border border-white/20 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Rejetées</p>
                  <p className="text-2xl font-bold text-red-600">{stats.rejected}</p>
                </div>
                <FontAwesomeIcon icon={faTimesCircle} className="text-3xl text-red-500" />
              </div>
            </div>
            <div className="bg-white/80 backdrop-blur-md rounded-xl shadow-lg border border-white/20 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">En Cours</p>
                  <p className="text-2xl font-bold text-blue-600">{stats.inProgress}</p>
                </div>
                <FontAwesomeIcon icon={faClock} className="text-3xl text-blue-500" />
              </div>
            </div>
            <div className="bg-white/80 backdrop-blur-md rounded-xl shadow-lg border border-white/20 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">En Attente</p>
                  <p className="text-2xl font-bold text-orange-600">{stats.pending}</p>
                </div>
                <FontAwesomeIcon icon={faClock} className="text-3xl text-orange-500" />
              </div>
            </div>
            <div className="bg-white/80 backdrop-blur-md rounded-xl shadow-lg border border-white/20 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Mis en Attente</p>
                  <p className="text-2xl font-bold text-yellow-600">{stats.hold}</p>
                </div>
                <FontAwesomeIcon icon={faPause} className="text-3xl text-yellow-500" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="relative z-10 p-4 sm:p-6 animate-slide-up">
        <div className="max-w-7xl mx-auto">
          {/* Search and Filter */}
          <div className="bg-white/80 backdrop-blur-md rounded-xl shadow-lg border border-white/20 p-4 sm:p-6 mb-6 overflow-hidden">
            <div className="space-y-4">
              {/* Search */}
              <div className="w-full overflow-hidden">
                <label className="block text-sm font-medium text-gray-700 mb-1">Recherche</label>
                <div className="flex gap-2 w-full min-w-0">
                  <input
                    type="text"
                    placeholder="Rechercher..."
                    value={localSearchTerm}
                    onChange={(e) => handleSearchInputChange(e.target.value)}
                    onKeyPress={handleSearchKeyPress}
                    className="flex-1 min-w-0 h-10 px-3 sm:px-4 text-sm sm:text-base border border-red-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none"
                    title="Rechercher par ID soumission, enquêteur, contrôleur"
                  />
                  <button
                    onClick={handleSearch}
                    className="h-10 w-10 sm:w-auto sm:px-4 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center justify-center flex-shrink-0"
                    title="Rechercher"
                  >
                    <FontAwesomeIcon icon={faSearch} />
                    <span className="hidden sm:inline ml-2">Rechercher</span>
                  </button>
                </div>
              </div>
              
              {/* Status and Date Filters */}
              <div className="flex flex-col sm:flex-row sm:items-end gap-4 flex-wrap">
                <div className="w-full sm:w-auto">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Statut</label>
                  <select
                    value={pendingStatusFilter}
                    onChange={(e) => setPendingStatusFilter(e.target.value)}
                    className="w-full sm:w-auto h-10 px-3 sm:px-4 text-sm sm:text-base border border-red-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none"
                  >
                    <option value="">Tous les statuts</option>
                    <option value="pending">En Attente</option>
                    <option value="in_progress">En Cours</option>
                    <option value="done">Contrôlées</option>
                    <option value="rejected">Rejetées</option>
                    <option value="hold">Mis en Attente</option>
                  </select>
                </div>

                <div className="w-full sm:w-auto">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date d'affectation</label>
                  <input
                    type="date"
                    value={pendingDateFilter}
                    onChange={(e) => setPendingDateFilter(e.target.value)}
                    className="w-full sm:w-auto h-10 px-3 sm:px-4 text-sm sm:text-base border border-red-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none"
                    title="Filtrer par date d'affectation"
                  />
                </div>

                <div className="w-full sm:w-auto">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date de contrôle</label>
                  <input
                    type="date"
                    value={pendingCheckDateFilter}
                    onChange={(e) => setPendingCheckDateFilter(e.target.value)}
                    className="w-full sm:w-auto h-10 px-3 sm:px-4 text-sm sm:text-base border border-red-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none"
                    title="Filtrer par date de contrôle"
                  />
                </div>

                <div className="w-full sm:w-auto">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Source</label>
                  <select
                    value={pendingSourceFilter}
                    onChange={(e) => setPendingSourceFilter(e.target.value)}
                    className="w-full sm:w-auto h-10 px-3 sm:px-4 text-sm sm:text-base border border-red-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none"
                    title="Filtrer par source du contrôle"
                  >
                    <option value="">Toutes</option>
                    <option value="human">Humain</option>
                    <option value="ai">IA</option>
                  </select>
                </div>

                <div className="flex gap-2 w-full sm:w-auto">
                  <button
                    onClick={handleApplyFilters}
                    className="h-10 px-4 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium"
                  >
                    Appliquer
                  </button>
                  <button
                    onClick={handleResetFilters}
                    className="h-10 px-4 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors text-sm"
                  >
                    Réinitialiser
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Submissions Table */}
          <div className="bg-white/80 backdrop-blur-md rounded-xl shadow-lg border border-white/20 overflow-hidden">
            {loading ? (
              <div className="p-8 text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
                <p className="mt-4 text-gray-600">Chargement des soumissions...</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        ID Soumission
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Enquêteur
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Contrôleur
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Statut
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date Affectation
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date de Contrôle
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Détails
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {submissions.length === 0 ? (
                      <tr>
                        <td colSpan="7" className="px-6 py-12 text-center text-gray-500">
                          Aucune soumission trouvée
                        </td>
                      </tr>
                    ) : (
                      submissions.map((submission) => {
                        // Synthetic external (IA / Resp. Contrôle) row
                        if (submission.is_external_row) {
                          const ext = submission.external_check || {};
                          const fmtDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR', {
                            year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
                          }) : '-';
                          const extStatus = ext.action === 'reject' ? 'rejected'
                            : ext.action === 'hold' ? (ext.is_ai ? 'pending' : 'hold')
                            : 'done';
                          return (
                            <tr key={submission.id} className="hover:bg-indigo-50 bg-indigo-50/30">
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                {submission.submission?.response_id || submission.f2f_submission?.response_id || submission.submission_id || submission.f2f_submission_id}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                <div className="flex items-center">
                                  <FontAwesomeIcon icon={faUser} className="mr-2 text-gray-400" />
                                  <div>
                                    <div>{submission.interviewer?.name || submission.supervisor?.name || submission.f2f_submission?.person?.name || 'N/A'}</div>
                                    <div className="text-xs text-gray-400">
                                      {submission.interviewer?.ref || submission.supervisor?.ref || submission.f2f_submission?.person?.ref || ''}
                                    </div>
                                  </div>
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                <div>
                                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-indigo-100 text-indigo-800">
                                    {ext.is_ai ? 'IA' : 'Resp. Contrôle'}
                                  </span>
                                  {ext.action === 'hold' && ext.reassigned_to && (
                                    <div className="text-xs text-gray-600 mt-1">→ {ext.reassigned_to}</div>
                                  )}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                {getStatusBadge(extStatus)}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">-</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                <div className="flex items-center">
                                  <FontAwesomeIcon icon={faCalendar} className="mr-2 text-indigo-400" />
                                  {fmtDate(ext.checked_at)}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm">
                                <div className="flex items-center gap-2">
                                  {(() => {
                                    const sub = submission.submission || submission.f2f_submission;
                                    return sub && submission.survey ? (
                                      <>
                                        {!submission.is_f2f && (
                                          <a
                                            href={`https://192.168.1.253/index.php?menu=monitoring`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-red-600 hover:text-red-800 transition-colors"
                                            title="Audio"
                                          >
                                            <FontAwesomeIcon icon={faVolumeUp} className="w-4 h-4" />
                                          </a>
                                        )}
                                        <a
                                          href={`https://defineresearch.online/index.php/admin/dataentry/sa/editdata/subaction/edit/surveyId/${submission.survey.limesurvey_id}/id/${sub.response_id}/browseLang`}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="text-red-600 hover:text-red-800 transition-colors"
                                          title="Questionnaire LimeSurvey"
                                        >
                                          <FontAwesomeIcon icon={faFileAlt} className="w-4 h-4" />
                                        </a>
                                      </>
                                    ) : null;
                                  })()}
                                  <button
                                    onClick={() => setSelectedAssignment({ ...submission, _is_external_view: true })}
                                    className="text-red-600 hover:text-red-800 transition-colors"
                                    title="Détails du contrôle"
                                  >
                                    <FontAwesomeIcon icon={faInfoCircle} className="w-4 h-4" />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        }

                        // Get latest check date - try multiple sources
                        let latestCheckDate = null;

                        // First try the latest_check_date from backend
                        if (submission.latest_check_date) {
                          latestCheckDate = submission.latest_check_date;
                        }
                        // Fallback to checks array
                        else if (submission.checks && Array.isArray(submission.checks) && submission.checks.length > 0) {
                          const sortedChecks = [...submission.checks].sort((a, b) =>
                            new Date(b.checked_at || 0) - new Date(a.checked_at || 0)
                          );
                          latestCheckDate = sortedChecks[0]?.checked_at;
                        }

                        return (
                          <tr key={submission.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                              {submission.submission?.response_id || submission.f2f_submission?.response_id || submission.submission_id || submission.f2f_submission_id}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              <div className="flex items-center">
                                <FontAwesomeIcon icon={faUser} className="mr-2 text-gray-400" />
                                <div>
                                  <div>{submission.interviewer?.name || submission.supervisor?.name || 'N/A'}</div>
                                  <div className="text-xs text-gray-400">
                                    {submission.interviewer?.ref || submission.supervisor?.ref || ''}
                                  </div>
                                  {submission.supervisor_id && !submission.interviewer_id && (
                                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                                      Prestataire
                                    </span>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {(() => {
                                const latestCheck = submission.checks?.[0];
                                const respCheck = submission.resp_controle_check;
                                if (latestCheck?.is_ai || respCheck?.is_ai) {
                                  return (
                                    <div>
                                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-indigo-100 text-indigo-800">
                                        IA
                                      </span>
                                      {respCheck?.action === 'hold' && respCheck?.reassigned_to && (
                                        <div className="text-xs text-gray-600 mt-1">
                                          → {respCheck.reassigned_to}
                                        </div>
                                      )}
                                    </div>
                                  );
                                }
                                if (respCheck) {
                                  return (
                                    <div>
                                      <div>{respCheck.controlled_by || 'N/A'}</div>
                                      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-800">
                                        Resp. Contrôle
                                      </span>
                                    </div>
                                  );
                                }
                                return submission.controller?.name || (
                                  <span className="text-gray-400">Non assigné</span>
                                );
                              })()}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              {getStatusBadge(getEffectiveStatus(submission))}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {submission.assigned_at ? (
                                <div className="flex items-center">
                                  <FontAwesomeIcon icon={faCalendar} className="mr-2 text-gray-400" />
                                  {new Date(submission.assigned_at).toLocaleDateString('fr-FR', {
                                    year: 'numeric',
                                    month: 'short',
                                    day: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })}
                                </div>
                              ) : (
                                <span className="text-gray-400">-</span>
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {latestCheckDate ? (
                                <div className="flex items-center">
                                  <FontAwesomeIcon icon={faCalendar} className="mr-2 text-gray-400" />
                                  {new Date(latestCheckDate).toLocaleDateString('fr-FR', {
                                    year: 'numeric',
                                    month: 'short',
                                    day: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })}
                                </div>
                              ) : (
                                <span className="text-gray-400">-</span>
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <div className="flex items-center gap-2">
                              {(() => {
                                const sub = submission.submission || submission.f2f_submission;
                                return sub && submission.survey ? (
                                  <>
                                    {!submission.is_f2f && (
                                      <a
                                        href={`https://192.168.1.253/index.php?menu=monitoring`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-red-600 hover:text-red-800 transition-colors"
                                        title="Audio"
                                      >
                                        <FontAwesomeIcon icon={faVolumeUp} className="w-4 h-4" />
                                      </a>
                                    )}
                                    <a
                                      href={`https://defineresearch.online/index.php/admin/dataentry/sa/editdata/subaction/edit/surveyId/${submission.survey.limesurvey_id}/id/${sub.response_id}/browseLang`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-red-600 hover:text-red-800 transition-colors"
                                      title="Questionnaire LimeSurvey"
                                    >
                                      <FontAwesomeIcon icon={faFileAlt} className="w-4 h-4" />
                                    </a>
                                  </>
                                ) : null;
                              })()}
                              <button
                                onClick={() => handleViewDetails(submission.id)}
                                className="text-red-600 hover:text-red-800 transition-colors"
                                title="Détails du contrôle"
                              >
                                <FontAwesomeIcon icon={faInfoCircle} className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Pagination */}
          {!loading && totalPages > 1 && (
            <div className="mt-6 flex items-center justify-between bg-white/80 backdrop-blur-md rounded-xl shadow-lg border border-white/20 p-4">
              <div className="flex items-center text-sm text-gray-700">
                <span>
                  Affichage de {((currentPage - 1) * submissionsPerPage) + 1} à {Math.min(currentPage * submissionsPerPage, totalSubmissions)} sur {totalSubmissions} soumission{totalSubmissions > 1 ? 's' : ''}
                </span>
              </div>
              
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <FontAwesomeIcon icon={faChevronLeft} className="w-4 h-4" />
                </button>
                
                <div className="flex space-x-1">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    
                    return (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                          currentPage === pageNum
                            ? 'bg-red-600 text-white'
                            : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>
                
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <FontAwesomeIcon icon={faChevronRight} className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Assignment Details Modal */}
      {selectedAssignment && selectedAssignment._is_external_view && (() => {
        const ext = selectedAssignment.external_check || {};
        const sub = selectedAssignment.submission || selectedAssignment.f2f_submission;
        const survey = selectedAssignment.survey;
        const fmt = (d) => d ? new Date(d).toLocaleString('fr-FR') : '-';
        return (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto p-4" {...assignmentDismiss}>
            <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full p-6" onClick={(e) => e.stopPropagation()}>
              <div className="flex justify-between items-start mb-4">
                <div className="flex items-center gap-2">
                  <span className="px-2 py-1 rounded text-xs font-semibold bg-indigo-100 text-indigo-700">
                    {ext.is_ai ? 'IA' : 'Resp. Contrôle'}
                  </span>
                  <h2 className="text-xl font-bold text-gray-800">Détails du contrôle</h2>
                </div>
                <button onClick={() => setSelectedAssignment(null)} className="text-gray-400 hover:text-gray-600">
                  <FontAwesomeIcon icon={faTimes} className="w-5 h-5" />
                </button>
              </div>

              {sub && survey && (
                <div className="mb-4 flex items-center gap-3">
                  <a href={`https://defineresearch.online/index.php/admin/dataentry/sa/editdata/subaction/edit/surveyId/${survey.limesurvey_id}/id/${sub.response_id}/browseLang`}
                    target="_blank" rel="noopener noreferrer"
                    className="text-red-600 hover:text-red-800 text-sm flex items-center gap-2">
                    <FontAwesomeIcon icon={faFileAlt} /> Questionnaire
                  </a>
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <div className="text-xs text-gray-500">Date de contrôle</div>
                  <div className="text-sm font-medium">{fmt(ext.checked_at)}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500">Action</div>
                  <div className="text-sm font-medium">
                    {ext.action === 'validate' ? 'Validé' : ext.action === 'reject' ? 'Rejeté' : 'En attente'}
                  </div>
                </div>
                <div className="sm:col-span-2">
                  <div className="text-xs text-gray-500">Enquête</div>
                  <div className="text-sm font-medium">{survey?.title || 'N/A'}</div>
                  <div className="text-xs text-gray-500">ID LimeSurvey: {survey?.limesurvey_id || 'N/A'}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500">ID Soumission</div>
                  <div className="text-sm font-medium">#{sub?.response_id || '-'}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500">Note</div>
                  <div className="text-sm font-medium">{ext.rating ? `${ext.rating}/5 ★` : '-'}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500">Raison</div>
                  <div className="text-sm font-medium">{ext.raison || '-'}</div>
                </div>
                {ext.action === 'hold' && ext.reassigned_to && (
                  <div>
                    <div className="text-xs text-gray-500">Réassigné à</div>
                    <div className="text-sm font-medium">{ext.reassigned_to}</div>
                  </div>
                )}
                {ext.action === 'reject' ? (
                  <div className="sm:col-span-2">
                    <div className="text-xs text-gray-500">Motif de rejet</div>
                    <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2 break-words">{ext.reject_reason || '-'}</div>
                  </div>
                ) : ext.comment && (
                  <div className="sm:col-span-2">
                    <div className="text-xs text-gray-500">Commentaire</div>
                    <div className="text-sm bg-gray-50 border border-gray-200 rounded p-2 break-words">{ext.comment}</div>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {selectedAssignment && !selectedAssignment._is_external_view && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full m-4 p-4 sm:p-6">
            <div className="flex justify-between items-start mb-4 gap-2">
              <h2 className="text-lg sm:text-xl font-bold text-gray-900 break-words flex-1">
                Détails de l'Affectation #{selectedAssignment.id}
              </h2>
              <button
                onClick={() => setSelectedAssignment(null)}
                className="text-gray-500 hover:text-gray-700 flex-shrink-0"
              >
                <FontAwesomeIcon icon={faTimes} className="w-5 h-5" />
              </button>
            </div>

            {/* Quick Links */}
            {(() => {
              const sub = selectedAssignment.submission || selectedAssignment.f2f_submission;
              return sub && selectedAssignment.survey ? (
                <div className="mb-4 flex items-center gap-3">
                  {!selectedAssignment.is_f2f && (
                    <a
                      href={`https://192.168.1.253/index.php?menu=monitoring`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-red-600 hover:text-red-800 transition-colors flex items-center gap-2 text-sm"
                      title="Audio"
                    >
                      <FontAwesomeIcon icon={faVolumeUp} />
                      <span>Audio</span>
                    </a>
                  )}
                  <a
                    href={`https://defineresearch.online/index.php/admin/dataentry/sa/editdata/subaction/edit/surveyId/${selectedAssignment.survey.limesurvey_id}/id/${sub.response_id}/browseLang`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-red-600 hover:text-red-800 transition-colors flex items-center gap-2 text-sm"
                    title="Questionnaire LimeSurvey"
                  >
                    <FontAwesomeIcon icon={faFileAlt} />
                    <span>Questionnaire</span>
                  </a>
                </div>
              ) : null;
            })()}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
              <div className="break-words">
                <p className="text-sm text-gray-500 mb-1">Soumission ID</p>
                <p className="font-medium text-sm sm:text-base">{selectedAssignment.submission?.response_id || selectedAssignment.f2f_submission?.response_id || selectedAssignment.submission_id || selectedAssignment.f2f_submission_id}</p>
              </div>
              {selectedAssignment.survey && (
                <div className="break-words">
                  <p className="text-sm text-gray-500 mb-1">Enquête</p>
                  <p className="font-medium text-sm sm:text-base break-words">{selectedAssignment.survey.title}</p>
                  <p className="text-xs text-gray-400 mt-1">ID: {selectedAssignment.survey.limesurvey_id}</p>
                </div>
              )}
              <div className="break-words">
                <p className="text-sm text-gray-500 mb-1">Statut</p>
                <div className="flex items-center">
                  {getStatusBadge(getEffectiveStatus(selectedAssignment))}
                </div>
              </div>
              {selectedAssignment.assigned_at && (
                <div className="break-words">
                  <p className="text-sm text-gray-500 mb-1">Date d'Affectation</p>
                  <p className="font-medium text-sm sm:text-base">
                    {new Date(selectedAssignment.assigned_at).toLocaleString('fr-FR')}
                  </p>
                </div>
              )}
              <div className="break-words">
                <p className="text-sm text-gray-500 mb-1">
                  {selectedAssignment.supervisor_id && !selectedAssignment.interviewer_id ? 'Prestataire' : 'Enquêteur'}
                </p>
                <p className="font-medium text-sm sm:text-base">
                  {selectedAssignment.interviewer?.name || selectedAssignment.supervisor?.name || 'N/A'}
                  {(selectedAssignment.interviewer?.ref || selectedAssignment.supervisor?.ref) && (
                    <span className="text-gray-500 ml-2">({selectedAssignment.interviewer?.ref || selectedAssignment.supervisor?.ref})</span>
                  )}
                </p>
                {selectedAssignment.supervisor_id && !selectedAssignment.interviewer_id && (
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800 mt-1">
                    Prestataire
                  </span>
                )}
                {selectedAssignment.interviewer?.supervisor && (
                  <div className="mt-1 text-xs text-gray-500">
                    Prestataire: <span className="font-medium text-gray-700">{selectedAssignment.interviewer.supervisor.name}</span> ({selectedAssignment.interviewer.supervisor.ref})
                  </div>
                )}
              </div>
              <div className="break-words">
                <p className="text-sm text-gray-500 mb-1">Contrôleur</p>
                {selectedAssignment.resp_controle_check ? (
                  <div>
                    <p className="font-medium text-sm sm:text-base">
                      {selectedAssignment.resp_controle_check.controlled_by || 'N/A'}
                    </p>
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-800 mt-1">
                      Resp. Contrôle
                    </span>
                  </div>
                ) : (
                  <p className="font-medium text-sm sm:text-base">
                    {selectedAssignment.controller?.name || (
                      <span className="text-gray-400 italic">Non assigné</span>
                    )}
                    {selectedAssignment.controller?.ref && (
                      <span className="text-gray-500 ml-2">({selectedAssignment.controller.ref})</span>
                    )}
                  </p>
                )}
              </div>
              {(selectedAssignment.submission?.submitted_at_utc || selectedAssignment.f2f_submission?.submitted_at_utc) && (
                <div className="break-words">
                  <p className="text-sm text-gray-500 mb-1">Date de Soumission</p>
                  <p className="font-medium text-sm sm:text-base">
                    {new Date(selectedAssignment.submission?.submitted_at_utc || selectedAssignment.f2f_submission?.submitted_at_utc).toLocaleString('fr-FR')}
                  </p>
                </div>
              )}
              {selectedAssignment.checks && selectedAssignment.checks.length > 0 && (() => {
                const latestCheck = selectedAssignment.checks.sort((a, b) => new Date(b.checked_at) - new Date(a.checked_at))[0];
                return latestCheck && latestCheck.checked_at ? (
                  <div className="break-words">
                    <p className="text-sm text-gray-500 mb-1">Date de Contrôle</p>
                    <p className="font-medium text-sm sm:text-base">
                      {new Date(latestCheck.checked_at).toLocaleString('fr-FR')}
                    </p>
                  </div>
                ) : null;
              })()}
            </div>

            {/* Control Checks */}
            {selectedAssignment.checks && selectedAssignment.checks.length > 0 && (
              <div>
                <h3 className="font-semibold mb-3 text-base sm:text-lg">Contrôles Effectués ({selectedAssignment.checks.length})</h3>
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {selectedAssignment.checks
                    .sort((a, b) => new Date(b.checked_at) - new Date(a.checked_at))
                    .map((check, index) => (
                      <div key={check.id || index} className="border rounded p-3 sm:p-4 bg-gray-50">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                          <div className="break-words">
                            <div className="text-xs text-gray-500 mb-1">Note</div>
                            <div className="text-sm font-medium text-gray-900">
                              {check.rating}/5 ⭐
                            </div>
                          </div>
                          <div className="break-words">
                            <div className="text-xs text-gray-500 mb-1">Action</div>
                            <div className="text-sm flex flex-wrap items-center gap-2">
                              <span className={`px-2 py-1 rounded-full text-xs ${
                                check.action === 'validate' ? 'bg-green-100 text-green-800' :
                                check.action === 'reject' ? 'bg-red-100 text-red-800' :
                                'bg-yellow-100 text-yellow-800'
                              }`}>
                                {check.action === 'validate' ? 'Validé' :
                                 check.action === 'reject' ? 'Rejeté' :
                                 'En attente'}
                              </span>
                            </div>
                          </div>
                          <div className="break-words">
                            <div className="text-xs text-gray-500 mb-1">Correction</div>
                            <div className="text-sm">
                              {check.correction_applied ? (
                                <span className="px-2 py-1 rounded-full text-xs bg-orange-100 text-orange-800">
                                  Correction appliquée
                                </span>
                              ) : (
                                <span className="px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-600">
                                  Aucune correction
                                </span>
                              )}
                            </div>
                          </div>
                          {check.comment && (
                            <div className="col-span-1 sm:col-span-2 break-words">
                              <div className="text-xs text-gray-500 mb-1">Commentaire</div>
                              <div className="text-sm text-gray-900 mt-1 bg-white p-2 rounded border break-words">
                                {check.comment}
                              </div>
                            </div>
                          )}
                          {check.reject_reason && (
                            <div className="col-span-1 sm:col-span-2 break-words">
                              <div className="text-xs text-gray-500 mb-1">Raison du Rejet</div>
                              <div className="text-sm text-red-900 mt-1 bg-red-50 p-2 rounded border border-red-200 break-words">
                                {check.reject_reason}
                              </div>
                            </div>
                          )}
                          {check.tel_value && (
                            <div className="break-words">
                              <div className="text-xs text-gray-500 mb-1">Téléphone</div>
                              <div className="text-sm text-gray-900">{check.tel_value}</div>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            )}

            {(!selectedAssignment.checks || selectedAssignment.checks.length === 0) && (
              <div className="text-center py-8 text-gray-500">
                Aucun contrôle effectué pour le moment
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default FullControlSubmissions;

